import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import NewsletterCompact from '../components/custom/NewsletterCompact';
import TeamTile from '../components/custom/TeamTile';
import PodcastSection from '../components/custom/PodcastSection';
import LatestArticleAlert from '../components/custom/LatestArticleAlert';
import RoadTo53Promo from '../components/custom/RoadTo53Promo';
import { ArticleCard, ArticleImage, TEAM_META, formatDate } from '../components/custom/ArticleCard';
import { useSeo } from '../components/custom/Seo';
import axios from 'axios';
import { logError } from '../lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const teams = [
  {
    team: 'Commanders',
    sport: 'NFL Football',
    path: '/commanders',
    image: 'https://images.unsplash.com/photo-1566349872260-a1d88307b698?w=600&h=800&fit=crop',
    primaryColor: '#5A1414',
    secondaryColor: '#FFB612',
  },
  {
    team: 'Capitals',
    sport: 'NHL Hockey',
    path: '/capitals',
    image: 'https://images.unsplash.com/photo-1681771701629-328d03c4ddf1?w=600&h=800&fit=crop',
    primaryColor: '#C8102E',
    secondaryColor: '#041E42',
  },
  {
    team: 'Nationals',
    sport: 'MLB Baseball',
    path: '/nationals',
    image: 'https://customer-assets.emergentagent.com/job_dcsportshub/artifacts/32zrk1eo_Nationals_Park_8.16.19_-_7.jpg',
    primaryColor: '#AB0003',
    secondaryColor: '#14225A',
  },
  {
    team: 'Wizards',
    sport: 'NBA Basketball',
    path: '/wizards',
    image: 'https://customer-assets.emergentagent.com/job_dcsportshub/artifacts/78ftbdpr_capital-one-arena-interior-2017-04-19-b4e30d-1024.jpg',
    primaryColor: '#002B5C',
    secondaryColor: '#E31837',
  },
  {
    team: 'Mystics',
    sport: 'WNBA Basketball',
    path: '/mystics',
    image: 'https://images.unsplash.com/photo-1504450758481-7338eba7524a?w=600&h=800&fit=crop',
    primaryColor: '#002B5C',
    secondaryColor: '#C8102E',
  },
  {
    team: 'DC Soccer',
    sport: 'United & Spirit',
    path: '/dcsoccer',
    image: 'https://customer-assets.emergentagent.com/job_dcsportshub/artifacts/wyb5vl7s_1920px-Audi_Field_exterior_2018-07-15_%2841744431650%29.jpg',
    primaryColor: '#000000',
    secondaryColor: '#EF3E42',
  },
];

const TEAM_SECTIONS = ['commanders', 'nationals', 'capitals', 'wizards', 'mystics', 'dcunited'];

const SectionHeader = ({ title, color = '#BF0A30', link, linkLabel }) => (
  <div className="flex items-end justify-between border-b border-slate-200 pb-3 mb-6">
    <h2 className="font-heading text-lg md:text-lg text-slate-900 uppercase tracking-wide flex items-center gap-3">
      <span className="inline-block w-1 h-6" style={{ backgroundColor: color }} />
      {title}
    </h2>
    {link && (
      <Link
        to={link}
        className="flex items-center gap-1 text-xs font-bold uppercase tracking-[0.15em] text-slate-500 hover:text-[#BF0A30] transition-colors"
      >
        {linkLabel || 'View All'}
        <ChevronRight className="w-4 h-4" />
      </Link>
    )}
  </div>
);

const Home = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useSeo({
    title: 'DC Sports Report | Washington DC Sports News & Podcast',
    description:
      'Independent coverage, analysis and conversation surrounding Washington, D.C. sports. Covering the Commanders, Nationals, Capitals, Wizards, Mystics, DC United and more.',
    path: '/',
  });

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await axios.get(`${API}/articles`);
        setArticles((response.data.articles || []).filter((a) => a.published !== false));
      } catch (error) {
        logError('Failed to load articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  const topStory = articles[0];
  const sideStories = articles.slice(1, 4);
  const latestNews = articles.slice(4, 12);
  const byTeam = TEAM_SECTIONS.map((key) => ({
    key,
    meta: TEAM_META[key],
    items: articles.filter((a) => a.team === key).slice(0, 3),
  })).filter((s) => s.items.length > 0);

  return (
    <div data-testid="home-page" className="min-h-screen bg-white">
      {/* Latest Article Alert */}
      <LatestArticleAlert />

      {/* Top Story */}
      {topStory && (
        <section data-testid="top-story-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            {/* Main story */}
            <Link
              to={`/article/${topStory.id}`}
              data-testid="top-story-link"
              className="md:col-span-8 group block"
            >
              <div className="aspect-video overflow-hidden border border-slate-200 bg-slate-100">
                <ArticleImage article={topStory} className="group-hover:scale-105 transition-transform duration-300" />
              </div>
              <div className="pt-4">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#BF0A30]">
                  Top Story · {(TEAM_META[topStory.team] || {}).label || 'DC Sports'}
                </span>
                <h1 className="font-serif-head font-extrabold text-3xl sm:text-4xl text-slate-900 leading-tight tracking-tight mt-2 group-hover:underline decoration-[3px] underline-offset-4">
                  {topStory.title}
                </h1>
                {(topStory.summary || topStory.content) && (
                  <p className="text-slate-600 text-base leading-relaxed mt-3 line-clamp-2 max-w-3xl">
                    {topStory.summary || topStory.content.substring(0, 220) + '…'}
                  </p>
                )}
                <p className="text-xs uppercase tracking-wide text-slate-500 mt-3">
                  {topStory.author} · {formatDate(topStory.created_at)}
                </p>
              </div>
            </Link>

            {/* Side stories */}
            {sideStories.length > 0 && (
              <div className="md:col-span-4 md:border-l md:border-slate-200 md:pl-8">
                <h2 className="font-heading text-base text-slate-900 uppercase tracking-[0.15em] border-b border-slate-200 pb-2 mb-4">
                  More Headlines
                </h2>
                <div className="divide-y divide-slate-200">
                  {sideStories.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      data-testid={`side-story-${article.id}`}
                      className="group flex gap-4 py-4 first:pt-0"
                    >
                      <div className="w-24 h-16 shrink-0 overflow-hidden border border-slate-200 bg-slate-100">
                        <ArticleImage article={article} />
                      </div>
                      <div className="min-w-0">
                        <span
                          className="text-[11px] font-bold uppercase tracking-[0.15em]"
                          style={{ color: (TEAM_META[article.team] || {}).color === '#000000' ? '#BF0A30' : (TEAM_META[article.team] || { color: '#BF0A30' }).color }}
                        >
                          {(TEAM_META[article.team] || {}).label}
                        </span>
                        <h3 className="font-serif-head font-bold text-sm text-slate-900 leading-snug mt-0.5 line-clamp-3 group-hover:underline underline-offset-2">
                          {article.title}
                        </h3>
                        <p className="text-[11px] uppercase tracking-wide text-slate-500 mt-1">
                          {formatDate(article.created_at)}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Road to 53 Promo */}
      <RoadTo53Promo />

      {/* Newsletter */}
      <NewsletterCompact />

      {/* Latest News */}
      {latestNews.length > 0 && (
        <section data-testid="latest-news-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <SectionHeader title="Latest News" />
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {latestNews.map((article) => (
              <ArticleCard key={article.id} article={article} showExcerpt={false} />
            ))}
          </div>
        </section>
      )}

      {/* Team Sections */}
      {byTeam.length > 0 && (
        <div className="bg-slate-50 border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
            {byTeam.map(({ key, meta, items }) => (
              <section key={key} data-testid={`team-section-${key}`}>
                <SectionHeader title={meta.full} color={meta.color === '#000000' ? '#BF0A30' : meta.color} link={meta.path} />
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                  {items.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      )}

      {/* Team Tiles */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <SectionHeader title="Your DC Teams" color="#002868" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
          {teams.map((team) => (
            <TeamTile key={team.team} {...team} />
          ))}
        </div>
      </section>

      {/* Podcast Section */}
      <PodcastSection />
    </div>
  );
};

export default Home;
